// Landscape: the tape is still 62mm wide; each label is 70mm along the feed,
// so the printed sticker is 70mm (wide) × 62mm (tall).
//
// NOTE: these two constants are the *legacy* fixed geometry consumed only by
// `solveLayout` (the printer-cli-era layout solver, unused by the frontend
// renderer). The in-app label renderer is now size-flexible — see
// `resolveLabelDimensions` / `LabelDimensionsMm` below.
export const LABEL_WIDTH_MM = 70;
export const LABEL_HEIGHT_MM = 62;

// --- Flexible label sizing (in-app renderer) ------------------------------
// The roll comes in two physical widths (the short, vertical side of the
// landscape sticker). On a 62mm roll we leave 1mm top + 1mm bottom so the
// inked content always lives inside a 60mm-tall band — that way a single
// layout serves both widths and the 62mm roll simply gains breathing room.
export const LABEL_ROLL_WIDTHS_MM = [60, 62] as const;
export type LabelRollWidthMm = (typeof LABEL_ROLL_WIDTHS_MM)[number];

// The length (long, horizontal side, along the feed) is freely configurable in
// this range so the user can match the die-cut / continuous-roll media loaded.
export const LABEL_LENGTH_MIN_MM = 72;
export const LABEL_LENGTH_MAX_MM = 100;

// Content always targets this band height so one layout serves both roll widths.
export const LABEL_USABLE_HEIGHT_MM = 60;

export const DEFAULT_LABEL_ROLL_WIDTH_MM: LabelRollWidthMm = 62;
export const DEFAULT_LABEL_LENGTH_MM = 72;

export interface LabelDimensionsMm {
  /** Horizontal extent (length along the feed). */
  widthMm: number;
  /** Vertical extent (physical roll width). */
  heightMm: number;
  /** Height of the centred content band. Defaults to heightMm when absent. */
  usableHeightMm?: number;
  /** Top offset that vertically centres the content band. Defaults to 0. */
  verticalOffsetMm?: number;
}

/** Clamps a requested length into the supported [min, max] range. */
export function clampLabelLengthMm(lengthMm: number): number {
  if (!Number.isFinite(lengthMm)) {
    return DEFAULT_LABEL_LENGTH_MM;
  }
  return Math.min(LABEL_LENGTH_MAX_MM, Math.max(LABEL_LENGTH_MIN_MM, Math.round(lengthMm)));
}

/** Snaps any roll width to the nearest supported value (60 or 62). */
export function normalizeRollWidthMm(rollWidthMm: number): LabelRollWidthMm {
  return rollWidthMm <= 61 ? 60 : 62;
}

// NOTE: `resolveLabelDimensions` / `DEFAULT_LABEL_DIMENSIONS` now live in
// `label-format-catalog.ts` — the catalog is the single resolution point and
// the clamp/snap helpers above survive only as its legacy fallback branch.

// The thermal head can't print to the edge; keep all inked content inside this
// border on every side (standard/large variants; compact and qr-only use
// tighter margins defined in the frontend layout module).
export const SAFE_MARGIN_MM = 3;

// --- Header (fox logo + "Prospero" wordmark) ------------------------------
export const HEADER_TOP_MM = 1.5;
export const FOX_SIZE_MM = 9;
export const WORDMARK_SIZE_MM = 5.5;

// When the tenant uploads its own logo it replaces the whole Prospero header
// (fox + wordmark) and is drawn "in fit" — scaled to contain inside this box,
// centred horizontally, anchored at HEADER_TOP_MM. The max height matches the
// fox (FOX_SIZE_MM) so the logo never extends below where the fox sits — that
// keeps it clear of the product name, whose first line starts just under it.
// Width is generous so wide wordmark-style logos still read well.
export const HEADER_LOGO_MAX_W_MM = 50;
export const HEADER_LOGO_MAX_H_MM = 9;

// --- Fields (Nome / Data prod. / Scadenza) --------------------------------
export const FIELD_LABEL_SIZE_MM = 2.4;
// Each field is "label on top, then an open writing area ending in a line you
// write the value on". WRITE_GAP (label -> its line) is the big gap — the
// room to handwrite a name/date — while the next label tucks up just under that
// line. So every label hugs the line that precedes it.
export const WRITE_GAP_MM = 9;
export const FIELD_SPACING_MM = 12.5;
export const HEADER_TO_FIELDS_MM = 5;
export const FIELD_LINE_QR_GAP_MM = 4;
export const RULE_STROKE_MM = 0.3;

// --- QR -------------------------------------------------------------------
// The VISIBLE (dark) QR spans exactly the writing-line block: from the first
// line to the last == 2 * FIELD_SPACING_MM.
export const QR_VISIBLE_SPAN_MM = 2 * FIELD_SPACING_MM;

// --- Footer (uuid + company line, stacked) --------------------------------
export const FOOTER_UUID_SIZE_MM = 2.0;
export const FOOTER_COMPANY_SIZE_MM = 1.7;
export const FOOTER_UUID_FROM_BOTTOM_MM = 5.5;
export const FOOTER_COMPANY_FROM_BOTTOM_MM = 2.0;
export const FOOTER_COMPANY_TEXT = 'BROSKI S.R.L. · P.IVA 09169870723 · prospero.food';

// --- Print pipeline -------------------------------------------------------
export const PRINT_ROTATION_DEG = 180;

// --- Value-below-label geometry (used by the frontend label preview) ------
// Gap between a field's label baseline and the value printed in the write area.
export const FIELD_VALUE_GAP_MM = 1.6;
// Font size for the value printed in the write area.
export const FIELD_VALUE_SIZE_MM = 3.2;
